import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { EventImagePicker } from "@/components/EventImagePicker";
import { QuoteCard } from "@/components/quotes/QuoteCard";

export const EventDialog = ({ open, onOpenChange, uid, timelineId, initial, entities, onSaved, readOnly, onAddJump, quotes = [], onNavigate }) => {
  const [form, setForm] = useState({
    title: initial?.title || "", description: initial?.description || "", display_date: initial?.display_date || "",
    timestamp_int: initial?.timestamp_int ?? "", location_entity_id: initial?.location_entity_id || "none", entity_ids: initial?.entity_ids || [], image_url: initial?.image_url || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const locations = entities.filter((e) => e.category === "Location");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, timestamp_int: parseInt(form.timestamp_int, 10), location_entity_id: form.location_entity_id === "none" ? null : form.location_entity_id };
    try {
      if (initial?.id) await api.patch(`/events/${initial.id}`, body);
      else await api.post(`/universes/${uid}/events`, { ...body, timeline_id: timelineId });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    await api.delete(`/events/${initial.id}`);
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto lf-scroll" data-testid="event-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{readOnly ? initial.title : initial?.id ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
          {readOnly && <p className="text-sm text-[#8B5CF6] mt-2" data-testid="event-inherited-note">Inherited from {initial.inherited_from}. Edit it on that timeline.</p>}
          <fieldset disabled={readOnly} className="space-y-4 py-6">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={set("title")} required data-testid="event-title-input" /></div>
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <div className="space-y-2"><Label>Display date</Label><Input value={form.display_date} onChange={set("display_date")} placeholder="3rd Moon, Year 412 AE" data-testid="event-display-date-input" /></div>
              <div className="space-y-2"><Label>Timestamp</Label><Input type="number" className="font-mono-lf" value={form.timestamp_int} onChange={set("timestamp_int")} required data-testid="event-timestamp-input" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={set("description")} data-testid="event-description-input" /></div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Select value={form.location_entity_id} onValueChange={(v) => setForm({ ...form, location_entity_id: v })}>
                <SelectTrigger data-testid="event-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unknown —</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Involved</Label>
              <EntityMultiSelect entities={entities.filter((e) => e.category !== "Location")} value={form.entity_ids} onChange={(v) => setForm({ ...form, entity_ids: v })} testid="event-entities" />
            </div>
            <EventImagePicker value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })}
              entities={entities.filter((e) => form.entity_ids.includes(e.id) || e.id === form.location_entity_id)} />
          </fieldset>
          {!readOnly && (
            <DialogFooter className="gap-2">
              {initial?.id && <Button type="button" variant="ghost" className="text-red-400 mr-auto" onClick={remove} data-testid="event-delete-button">Delete</Button>}
              {initial?.id && onAddJump && <Button type="button" variant="outline" className="border-[#8B5CF6]/50 text-[#C4B5FD]" onClick={() => { onOpenChange(false); onAddJump(initial); }} data-testid="event-add-jump-button">⧗ Add jump</Button>}
              <Button type="submit" disabled={busy} data-testid="event-save-button">{busy ? "Saving…" : "Save event"}</Button>
            </DialogFooter>
          )}
        </form>
        {quotes.length > 0 && (
          <section className="mt-6 border-t border-[#2D3442] pt-5" data-testid="event-quotes">
            <div className="lf-label mb-3">Quotes said here · {quotes.length}</div>
            <div className="space-y-3">{quotes.map((q) => <QuoteCard key={q.id} q={q} onNavigate={onNavigate} canEdit={false} />)}</div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
};
